import { imagesApi } from '@/api/images';
import type { ImageAsset } from '@/api/types/core/image';
import { computed, ref } from 'vue';

export function useMemoImages(memoUuid: string) {
  const images = ref<ImageAsset[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Charger les images du mémo
  const fetchImages = async () => {
    if (!memoUuid) return;
    
    loading.value = true;
    error.value = null;
    
    try {
      const response = await imagesApi.getEntityImages(memoUuid, 'memo');
      if (response.success && response.data) {
        images.value = response.data;
      } else {
        error.value = response.error || 'Erreur lors du chargement des images';
      }
    } catch (err) {
      error.value = 'Erreur lors du chargement des images';
      console.error('Erreur fetchImages:', err);
    } finally {
      loading.value = false;
    }
  };

  // Uploader une image pour le mémo
  const uploadImage = async (file: File) => {
    if (!memoUuid) {
      throw new Error('UUID du mémo requis');
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await imagesApi.uploadImage(file, memoUuid, 'memo');
      if (response.success && response.data) {
        // Recharger les images après upload
        await fetchImages();
        return response.data;
      } else {
        const errorMessage = response.error || 'Erreur lors de l\'upload';
        error.value = errorMessage;
        throw new Error(errorMessage);
      }
    } catch (err) {
      error.value = 'Erreur lors de l\'upload';
      console.error('Erreur uploadImage:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Supprimer une image
  const deleteImage = async (imageUuid: string) => {
    if (!memoUuid) {
      throw new Error('UUID du mémo requis');
    }

    loading.value = true;
    error.value = null;

    try {
      await imagesApi.deleteImage(imageUuid);
      // Recharger les images après suppression
      await fetchImages();
    } catch (err) {
      error.value = 'Erreur lors de la suppression';
      console.error('Erreur deleteImage:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Obtenir l'URL d'une image
  const getImageUrl = (imageAsset: ImageAsset): string => {
    return imagesApi.getImageUrl(imageAsset.uuid);
  };

  // Obtenir l'URL de la miniature
  const getThumbnailUrl = (imageAsset: ImageAsset): string => {
    return imagesApi.getThumbnailUrl(imageAsset.uuid);
  };

  // Vérifier s'il y a des images
  const hasImages = computed(() => images.value.length > 0);

  return {
    images,
    loading,
    error,
    fetchImages,
    uploadImage,
    deleteImage,
    getImageUrl,
    getThumbnailUrl,
    hasImages,
  };
} 